import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { fixtureOn } from './fixture';
import { getSession } from './api';
import type { Session } from './types';

/**
 * Due sessioni, e non sono la stessa cosa.
 *
 *  - la sessione **Supabase**: chi sei. Vive nel localStorage, la gestisce l'SDK.
 *  - la sessione **comunale** (`municipal_session()`): per quale ente puoi
 *    agire, con che limiti e con quanta quota residua. Vive sul server e si
 *    ricarica spesso, perché quota e fascia oraria cambiano da soli mentre la
 *    scheda è aperta.
 *
 * Essere autenticati non implica essere abilitati: un cittadino qualunque con
 * un account dell'app può arrivare qui e ottenere `null`. Non è un errore, è
 * una schermata.
 */

interface AuthState {
  userId: string | null;
  email: string | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  userId: null,
  email: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    userId: null,
    email: null,
    loading: true,
  });

  useEffect(() => {
    if (fixtureOn) {
      // Nessun login in fixture: le schermate interne devono essere
      // raggiungibili senza un ente abilitato su Supabase.
      setState({ userId: 'fixture', email: 'fixture@example.invalid', loading: false });
      return;
    }
    if (!supabase) {
      setState({ userId: null, email: null, loading: false });
      return;
    }

    const client = supabase;

    /* Ritorno dal magic link, forma nuova.

       Dal 19/08/2026 il link dell'email non passa più dal `verify` di Supabase
       ma punta dritto alla destinazione con `?token_hash=…&type=magiclink`
       (il template è uno solo per tutto il progetto: la ragione del cambio sta
       nell'app, vedi `lib/supabase.ts`). `detectSessionInUrl` sa leggere il
       frammento `#access_token=` e il `?code=` PKCE, ma NON questo: se non lo
       spendiamo noi, l'operatore torna sulla pagina di accesso senza capire
       perché.

       L'indirizzo si ripulisce sempre, anche quando la verifica fallisce: un
       token monouso non deve restare nella barra, né finire in un segnalibro o
       nella cronologia. */
    async function consumeTokenHash(): Promise<void> {
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get('token_hash');
      if (!tokenHash) return;

      /* Tre tipi, una sola rotta. `invite` è quello del primo accesso: dal
         02/09/2026 l'operatore non nasce più in silenzio nel pannello — viene
         invitato, e l'invito ha il suo template. Un valore che non
         riconosciamo si tratta come `magiclink` invece di passare a Supabase
         una stringa arrivata dall'indirizzo. */
      const richiesto = params.get('type');
      const type =
        richiesto === 'signup' || richiesto === 'invite' ? richiesto : 'magiclink';
      try {
        await client.auth.verifyOtp({ token_hash: tokenHash, type });
      } finally {
        params.delete('token_hash');
        params.delete('type');
        const query = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
        );
      }
    }

    // getSession() risolve anche il caso "sono appena tornato dal magic link":
    // per il `?code=` PKCE e per il frammento l'SDK ha già fatto da sé prima
    // che React monti; il `token_hash` lo spendiamo qui, e solo dopo si guarda
    // chi c'è — altrimenti il primo giro direbbe "nessuno" e sfarfallerebbe la
    // schermata di accesso.
    void consumeTokenHash()
      .catch(() => undefined)
      .then(() => client.auth.getSession())
      .then(({ data }) => {
        setState({
          userId: data.session?.user.id ?? null,
          email: data.session?.user.email ?? null,
          loading: false,
        });
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setState({
        userId: session?.user.id ?? null,
        email: session?.user.email ?? null,
        loading: false,
      });
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

/**
 * Ente, limiti e quota.
 *
 * `refetchInterval` di un minuto e refetch al ritorno sulla scheda: durante un
 * evento la console resta aperta per ore, e una quota mostrata alle 22:00 che
 * dice ancora «2 di 2» alle 23:30 è peggio di nessuna quota. Costa una RPC al
 * minuto per operatore — su decine di operatori, niente.
 */
export function useMunicipal() {
  const { userId } = useAuth();
  return useQuery<Session | null>({
    queryKey: ['municipal-session', userId],
    queryFn: getSession,
    enabled: Boolean(userId),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}
